import React, { useCallback, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  UncontrolledDropdown,
} from "reactstrap";
import LOASummaryWidgets from "./LOASummaryWidgets";

import {
  buildListParams,
  parseFilter,
  parsePage,
  parsePageSize,
  parseSearch,
  parseSort,
} from "common/lib/listUrlParams";
import { getSortObj } from "common/lib/tableDataOptions";
import { deleteLOA, getLOAs } from "common/services/govt-requests.service";
import TableContainer from "../../Components/Common/TableContainer";

import Permission from "Components/Common/Permission";
import Permissions from "Components/Common/Permissions";

import BreadCrumb from "Components/Common/BreadCrumb";
import BulkActionBar from "Components/Common/BulkActionBar";
import DeleteModal from "Components/Common/DeleteModal";
import FileIcon from "Components/Common/FileIcon";
import { useProfile } from "Components/Hooks/UserHooks";
import { normalize } from "common/lib/normalize";
import { isAllowed } from "common/lib/permissionUtils";
import * as moment from "moment";
import { toast } from "react-toastify";
import Filters from "./Filters";
import Import from "./Import";

interface DateRange {
  fromDate: Date | null;
  toDate: Date | null;
}

interface FilterValue {
  agency: string;
  dateRange: DateRange;
  region: string;
  mda: string;
}

const defaultSort = [{ id: "createdAt", desc: true }];

const defaultFilterValue: FilterValue = {
  region: "",
  mda: "",
  agency: "",
  dateRange: {
    fromDate: null,
    toDate: null,
  },
};

const LOAs = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [params] = useState({
    page: parsePage(searchParams),
    pageSize: parsePageSize(searchParams),
  });

  const [pagination, setPagination] = useState({
    pageIndex: params.page - 1, // Convert to zero-based index
    pageSize: params.pageSize,
  });

  // Update pageIndex
  const setPage = (newPage: number) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      pageIndex: newPage - 1,
    }));
  };

  // Update pageSize
  // const setPageSize = (newPageSize: number) => {
  //   setPagination((prevPagination) => ({
  //     ...prevPagination,
  //     pageSize: newPageSize,
  //   }));
  // };

  const [sorted, setSorted] = useState(() => parseSort(searchParams.get("sort"), defaultSort));

  const [selectedRecord, setSelectedRecord] = useState<any>({});

  const [searchValue, setSearchValue] = useState(parseSearch(searchParams));
  
  const [filterValue, setFilterValue] = useState(() => parseFilter(searchParams, defaultFilterValue));
  const unCheckAll = useCallback(() => {
    const checkall: any = document.getElementById("checkBoxAll");
    if (checkall) checkall.checked = false;
    setSelectedCheckBox([]);
  }, []);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState<any>({});
  const [apiParams, setApiParams] = useState<any>({});
  const onPageChange = useCallback(
    async ({
      page,
      sorted,
      searchValue,
      filterValue,
    }: {
      page: number;
      sorted: Array<{ id: string; desc: boolean }>;
      searchValue: string;
      filterValue?: any;
    }) => {
      const params = buildListParams(searchParams, {
        page,
        pageSize: pagination.pageSize,
        sorted,
        searchValue,
        filterValue,
        defaultFilterValue,
        defaultSort,
      });
      setSearchParams(params, {
        replace: true,
      });

      const sortObj = getSortObj(sorted);

      let andConditions = [];

      if (filterValue?.agency) {
        andConditions.push({
          agency: {
            id: {
              $eq: filterValue.agency.value,
            },
          },
        });
      }
      if (filterValue?.region) {
        andConditions.push({
          mda: {
            region: {
              id: {
                $eq: filterValue.region.value,
              },
            },
          },
        });
      }
      if (filterValue?.mda) {
        andConditions.push({
          mda: {
            id: {
              $eq: filterValue.mda.value,
            },
          },
        });
      }
      if (filterValue?.dateRange?.fromDate && filterValue?.dateRange?.toDate) {
        andConditions.push({
          $or: [
            {
              startDate: {
                $between: [
                  filterValue.dateRange.fromDate,
                  filterValue.dateRange.toDate,
                ],
              },
            },
            {
              endDate: {
                $between: [
                  filterValue.dateRange.fromDate,
                  filterValue.dateRange.toDate,
                ],
              },
            },
          ],
        });
      }

      andConditions.push({
        $or: [
          {
            requestTitle: {
              $containsi: searchValue,
            },
          },
          {
            mda: {
              name: {
                $containsi: searchValue,
              },
            },
          },
          {
            mda: {
              region: {
                name: {
                  $containsi: searchValue,
                },
              },
            },
          },
        ],
      });

      try {
        setLoading(true);

        const APIParams = {
          pagination: {
            page,
            pageSize: pagination.pageSize,
          },
          sort: sortObj,
          populate: [
            "agency",
            "mda",
            "mda.region",
            "individual",
            "positionType",
            "typeOfRequest",
          ],
          filters: {
            $and: andConditions,
          },
        };
        setApiParams(APIParams);
        unCheckAll();
        const response: any = await getLOAs(APIParams);
        setData(normalize(response.data));
        setMeta(response?.meta);
      } catch (e: any) {
        toast.error("Error while fetching data", {});
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize, searchParams, setSearchParams, unCheckAll]
  );

  const [deleteModal, setDeleteModal] = useState(false);

  const onClickDelete = useCallback(
    (data: any) => {
      setSelectedRecord(data);
      setDeleteModal(true);
    },
    [setSelectedRecord, setDeleteModal]
  );

  const fetchUpdatedData = useCallback(() => {
    onPageChange({
      page: pagination.pageIndex + 1,
      sorted: sorted,
      searchValue: searchValue,
    });
  }, [onPageChange, pagination.pageIndex, sorted, searchValue]);

  const [deleting, setDeleting] = useState(false);
  const handleDelete = async () => {
    if (selectedRecord) {
      setDeleting(true);
      try {
        await deleteLOA(selectedRecord.id);
        fetchUpdatedData();
        setDeleting(false);
        setDeleteModal(false);
        toast.success("LOA deleted successfully");
      } catch (e: any) {
        toast.error("Error while deleting LOA");
      } finally {
        setDeleting(false);
      }
    }
  };

  const checkedAll = useCallback(() => {
    const checkall: any = document.getElementById("checkBoxAll");
    const ele = document.querySelectorAll(".tableCheckbox");

    if (checkall.checked) {
      ele.forEach((ele: any) => {
        ele.checked = true;
      });
    } else {
      ele.forEach((ele: any) => {
        ele.checked = false;
      });
    }
    onSelectCheckbox();
  }, []);

  const [selectedCheckBox, setSelectedCheckBox] = useState<any>([]);

  const onSelectCheckbox = () => {
    const ele = document.querySelectorAll(".tableCheckbox:checked");
    setSelectedCheckBox(ele);
  };

  /* Delete Multiple */
  const [deleteMultipleModal, setDeleteMultipleModal] = useState(false);
  const handleDeleteMultiple = async () => {
    const selectedIds = Array.from(selectedCheckBox).map((ele: any) => {
      return ele.value;
    });
    if (selectedIds.length > 0) {
      setDeleting(true);
      try {
        await Promise.all(
          selectedIds.map((id: any) => {
            return deleteLOA(id);
          })
        );
        setDeleting(false);
        toast.success("LOAs deleted successfully.");
        fetchUpdatedData();
        setDeleteMultipleModal(false);
      } catch (e: any) {
        toast.error("Error while deleting data.");
      } finally {
        setDeleting(false);
      }
    }
  };

  const { userProfile } = useProfile();

  const [columnVisibility] = useState({
    checkBoxColumn: isAllowed(userProfile?.permissions, [
      {
        resource: "government-request",
        action: "delete",
        controller: "loa",
      },
    ]),
    updatedAt: isAllowed(userProfile?.permissions, [
      {
        resource: "government-request",
        action: "update",
        controller: "loa",
      },
    ]),
    createdAt: isAllowed(userProfile?.permissions, [
      {
        resource: "government-request",
        action: "create",
        controller: "loa",
      },
    ]),
  });

  const columns = useMemo(
    () => [
      {
        header: (
          <input
            type="checkbox"
            id="checkBoxAll"
            className="form-check-input"
            onClick={() => checkedAll()}
          />
        ),
        cell: (cell: any) => {
          return (
            <input
              type="checkbox"
              className="tableCheckbox form-check-input"
              value={cell.getValue()}
              onChange={() => onSelectCheckbox()}
            />
          );
        },
        id: "checkBoxColumn",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Ministry/Department/Agency (MDA)",
        accessorKey: "mda.name",
        id: "mda.name",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const rowData = cell.row.original;

          return (
            <>
              <div
                className="d-flex align-items-center"
                style={{
                  // whiteSpace: "pre-wrap",
                  width: "400px",
                }}
              >
                <div className="flex-shrink-0 me-3">
                  <div className="avatar-sm">
                    <div className="avatar-title border bg-light text-danger rounded fs-24">
                      <FileIcon
                        fileName={
                          rowData?.reportFile &&
                          Array.isArray(rowData?.reportFile) &&
                          rowData?.reportFile[0]?.name
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className="flex-grow-1">
                  <h5 className="fs-14 mb-1" style={{ whiteSpace: "pre-wrap" }}>
                    <Permission
                      alternative={
                        <Link to={`/loas/${rowData.id}`} className="text-body">
                          {cell.getValue()}
                          {cell.row.original?.mda?.acronym
                            ? ` (${cell.row.original?.mda?.acronym})`
                            : ""}
                        </Link>
                      }
                      action="findOne"
                      resource="mda"
                    >
                      <Link to={`/loas/${rowData.id}`} className="text-body">
                        {cell.getValue()}
                        {cell.row.original?.mda?.acronym
                          ? ` (${cell.row.original?.mda?.acronym})`
                          : ""}
                      </Link>
                    </Permission>
                  </h5>
                  <p className="text-muted mb-0">
                    <span className="fw-medium">
                      {cell.row.original?.mda?.region?.name || ""}
                    </span>
                  </p>
                </div>
              </div>
            </>
          );
        },
      },
      // {
      //   header: "Type of LOA",
      //   accessorKey: "typeOfRequest.name",
      //   enableColumnFilter: false,
      //   id: "typeOfRequest.name",
      //   cell: (cell: any) => {
      //     return <>{cell.getValue() || "Not Provided"}</>;
      //   },
      // },
      {
        header: "Period",
        accessorKey: "periodInMonths",
        enableColumnFilter: false,
        id: "periodInMonths",
        cell: (cell: any) => {
          const row = cell?.row?.original || {};
          const start = row?.startDate ? new Date(row.startDate) : null;
          const end = row?.endDate ? new Date(row.endDate) : null;

          if (
            !start ||
            !end ||
            isNaN(start.getTime()) ||
            isNaN(end.getTime())
          ) {
            return "Not Provided";
          }

          let months =
            (end.getFullYear() - start.getFullYear()) * 12 +
            (end.getMonth() - start.getMonth());
          if (end.getDate() < start.getDate()) {
            months -= 1;
          }

          if (months < 0) return "Not Provided";

          const label = months === 1 ? "Month" : "Months";
          return (
            <>
              <h5 className="fs-14 my-1 fw-normal">{months}</h5>
              <span className="text-muted">{label}</span>
            </>
          );
        },
      },

      {
        header: "Reporting Agency",
        accessorKey: "agency.name",
        enableColumnFilter: false,
        id: "agency.name",
        cell: (cell: any) => {
          return <>{cell.getValue() || "Not Provided"}</>;
        },
      },

      {
        header: "Value of Request",
        accessorKey: "totalValueOfRequest",
        enableColumnFilter: false,
        id: "totalValueOfRequest",
        cell: (cell: any) => {
          return (
            <>
              {cell.getValue()
                ? `$ ${cell.getValue().toLocaleString()}`
                : "Not Provided"}
            </>
          );
        },
      },
      {
        header: "Start Date",
        accessorKey: "startDate",
        id: "startDate",
        enableColumnFilter: false,
        cell: (cell: any) => {
          if (cell.getValue() === null || cell.getValue() === undefined)
            return "Not Provided";
          const date = moment(new Date(cell?.getValue())).format("DD MMM Y");
          return <>{date}</>;
        },
      },
      {
        header: "End Date",
        accessorKey: "endDate",
        id: "endDate",
        enableColumnFilter: false,
        cell: (cell: any) => {
          if (cell.getValue() === null || cell.getValue() === undefined)
            return "Not Provided";
          const date = moment(new Date(cell?.getValue())).format("DD MMM Y");
          return <>{date}</>;
        },
      },

      //   {
      //     header: "Start Date",
      //     accessorKey: "startDate",
      //     id: "startDate",
      //     enableColumnFilter: false,
      //     cell: (cell: any) => {
      //       if (cell.getValue() === null) return null;
      //       const date = moment(new Date(cell?.getValue())).format("DD MMM Y");
      //       return <>{date}</>;
      //     },
      //   },

      //   {
      //     header: "End Date",
      //     accessorKey: "endDate",
      //     id: "endDate",
      //     enableColumnFilter: false,
      //     cell: (cell: any) => {
      //       if (cell.getValue() === null) return null;
      //       const date = moment(new Date(cell?.getValue())).format("DD MMM Y");
      //       return <>{date}</>;
      //     },
      //   },

      {
        header: "Action",
        enableSorting: false,
        cell: (cellProps: any) => {
          const rowData = cellProps.row.original;
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                href="#"
                className="btn btn-soft-secondary btn-sm dropdown"
                tag="button"
              >
                <i className="ri-more-fill align-middle"></i>
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-end">
                <Permission
                  resource="government-request"
                  action="findOne"
                  controller="loa"
                >
                  <DropdownItem
                    href={`/loas/${rowData.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/loas/${rowData.id}`);
                    }}
                  >
                    <i className="ri-eye-fill align-bottom me-2 text-muted"></i>{" "}
                    View
                  </DropdownItem>
                </Permission>

                <Permission
                  resource="government-request"
                  action="update"
                  controller="loa"
                >
                  <DropdownItem
                    href="/#"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/loas/update/${rowData.id}`);
                    }}
                  >
                    <i className="ri-pencil-fill align-bottom me-2 text-muted"></i>{" "}
                    Edit
                  </DropdownItem>
                </Permission>

                <Permission
                  resource="government-request"
                  action="delete"
                  controller="loa"
                >
                  <DropdownItem divider />
                  <DropdownItem
                    href="#"
                    onClick={() => {
                      onClickDelete(rowData);
                    }}
                  >
                    <i className="ri-delete-bin-fill align-bottom me-2 text-muted"></i>{" "}
                    Delete
                  </DropdownItem>
                </Permission>
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [checkedAll, navigate, onClickDelete]
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="LOAs (Letters of Agreement)"
            pageTitles={[{ title: "Dashboard", link: "/dashboard" }]}
          />

          <DeleteModal
            show={deleteModal}
            onDeleteClick={() => handleDelete()}
            onCloseClick={() => setDeleteModal(false)}
            loading={deleting}
          />
          <LOASummaryWidgets />

          <Filters
            setSearchValue={setSearchValue}
            setPage={setPage}
            searchValue={searchValue}
            setFilterValue={setFilterValue}
            filterValue={filterValue}
            defaultFilterValue={defaultFilterValue}
            setSorted={setSorted}
          />
          <Card>
            <CardHeader>
              <Row className="align-items-center g-3">
                <Col md={8}>
                  <h5 className="card-title mb-1">
                    LOAs (Letters of Agreement)
                  </h5>
                  <p className="mb-0 text-muted">
                    A list of Letters of Agreement (LOAs) between UN Agencies
                    and ministries, departments and agencies.
                  </p>
                </Col>
                <div className="col-md-auto ms-auto">
                  <div className="d-flex gap-2">
                    <Permission resource="government-request" action="create">
                      <Link
                        to="#"
                        className="btn btn-primary"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate("/loas/add");
                        }}
                      >
                        <i className="ri-add-line align-bottom me-1"></i>
                        Add LOA
                      </Link>
                    </Permission>

                    <Permissions
                      checks={[
                        {
                          resource: "government-request",
                          action: "exportLOAsToCSV",
                          controller: "loa",
                        },
                        {
                          resource: "government-request",
                          action: "import",
                          controller: "loa",
                        },
                      ]}
                    >
                      <Import apiParams={apiParams} fetchData={fetchUpdatedData} />
                    </Permissions>
                  </div>
                </div>
              </Row>
            </CardHeader>
            <CardBody>
              <TableContainer
                loading={loading}
                columns={columns}
                data={data || []}
                pagination={pagination}
                setPagination={setPagination}
                sorted={sorted}
                setSorted={setSorted}
                onPageChange={onPageChange}
                FilterSection={() => {}}
                totalRows={meta?.pagination?.total || 0}
                totalPages={meta?.pagination?.pageCount || 0}
                searchValue={searchValue}
                filterValue={filterValue}
                columnVisibility={columnVisibility}
                divClass="table-responsive table-card mb-4"
                tableClass="align-middle table-nowrap table-striped mb-0"
                theadClass="table-light table-nowrap"
              />
              <DeleteModal
                show={deleteMultipleModal}
                onDeleteClick={() => handleDeleteMultiple()}
                onCloseClick={() => setDeleteMultipleModal(false)}
                loading={deleting}
              />
            </CardBody>
          </Card>
        </Container>
        <Permissions
          checks={[
            {
              resource: "government-request",
              action: "delete",
              controller: "loa",
            },
          ]}
        >
          <BulkActionBar
            count={selectedCheckBox.length}
            onClear={unCheckAll}
            itemLabel="LOA"
          >
            <Button
              color="danger"
              size="sm"
              className="material-shadow-none"
              onClick={() => setDeleteMultipleModal(true)}
            >
              <i className="ri-delete-bin-line align-bottom me-1"></i> Delete
            </Button>
          </BulkActionBar>
        </Permissions>
      </div>
    </React.Fragment>
  );
};

export default LOAs;
